/**
 * Care Minutes Dashboard - Enterprise Grade
 * Real-time tracking of care minutes compliance for Aged Care Act 2024
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Clock, AlertTriangle, CheckCircle, TrendingUp, TrendingDown,
  Users, Activity, Target, Calendar, BarChart3, Download, Filter,
  RefreshCw, Eye, FileText, ArrowRight, ArrowLeft, Settings, Search
} from 'lucide-react';
import CareMinutesService from '../../services/care/CareMinutesService';
import { useAppContext } from '../../context/AppContext';
import { useToast } from '../Common/ToastProvider';
import { residents } from '../../data/mockData';

const CareMinutesDashboard = ({ facilityId, date = null }) => {
  const { state } = useAppContext();
  const { showToast } = useToast();
  const selectedFacility = facilityId || state.selectedFacility;
  const targetDate = date || new Date().toISOString().split('T')[0];
  
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [residentDetails, setResidentDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('today'); // today, week, month
  const [selectedDate, setSelectedDate] = useState(targetDate);
  const [filterWard, setFilterWard] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedResident, setSelectedResident] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const summaryResult = await CareMinutesService.getFacilityComplianceSummary(
        selectedFacility,
        selectedDate
      );
      
      const alertsResult = await CareMinutesService.getComplianceAlerts(
        selectedFacility,
        selectedDate
      );

      // Get resident-level details
      const allResidents = residents.filter(r => r.facility === selectedFacility);
      const detailsPromises = allResidents.map(async (resident) => {
        const cmResult = await CareMinutesService.getByResidentAndDate(resident.id, selectedDate);
        return {
          resident,
          careMinutes: cmResult.success ? cmResult.data : null
        };
      });
      const details = await Promise.all(detailsPromises);
      
      if (summaryResult.success) {
        setSummary(summaryResult.data);
      } else {
        console.warn('[CareMinutesDashboard] Summary result failed:', summaryResult.error);
        // Set empty summary if no data
        setSummary({
          date: selectedDate,
          facilityId: selectedFacility,
          totalResidents: 0,
          compliantResidents: 0,
          rnCompliantResidents: 0,
          fullyCompliantResidents: 0,
          complianceRate: 0,
          averageMinutes: 0,
          averageRNMinutes: 0,
          totalMinutes: 0,
          totalRNMinutes: 0,
          targetMinutes: 0,
          targetRNMinutes: 0
        });
      }
      
      if (alertsResult.success) {
        setAlerts(alertsResult.data);
      } else {
        setAlerts([]);
      }

      setResidentDetails(details);
    } catch (error) {
      console.error('[CareMinutesDashboard] Error loading care minutes data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedFacility, selectedDate, selectedView, filterWard]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  
  const handleRefresh = async () => {
    setLoading(true);
    console.log('[CareMinutesDashboard] Force refreshing data...');
    try {
      const result = await CareMinutesService.forceReinitialize();
      if (result.success) {
        console.log(`[CareMinutesDashboard] Refreshed ${result.count} of ${result.total} records`);
        // Wait a moment for storage to update
        await new Promise(resolve => setTimeout(resolve, 200));
      } else {
        console.error('[CareMinutesDashboard] Refresh failed:', result.error);
        showToast(`Failed to refresh data: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error('[CareMinutesDashboard] Error refreshing:', error);
      showToast(`Error refreshing data: ${error.message}`, 'error');
    } finally {
      await loadData();
    }
  };

  const handleDateChange = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate.toISOString().split('T')[0]);
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleExportData = async (format) => {
    try {
      const data = {
        facilityId: selectedFacility,
        date: selectedDate,
        summary,
        residentDetails: residentDetails.filter(d => d.careMinutes),
        exportedAt: new Date().toISOString()
      };

      if (format === 'csv') {
        // Generate CSV
        const csv = generateCSV(data);
        downloadFile(csv, `care-minutes-${selectedDate}.csv`, 'text/csv');
      } else if (format === 'pdf') {
        // Generate PDF (simulated)
        showToast('PDF export functionality coming soon', 'info');
      } else {
        // JSON export
        const json = JSON.stringify(data, null, 2);
        downloadFile(json, `care-minutes-${selectedDate}.json`, 'application/json');
      }
      
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting data:', error);
      showToast('Error exporting data', 'error');
    }
  };

  const generateCSV = (data) => {
    const headers = ['Resident ID', 'Name', 'Total Minutes', 'RN Minutes', 'Target Minutes', 'Target RN', 'Compliant', 'RN Compliant', 'Gap', 'RN Gap'];
    const rows = data.residentDetails
      .filter(d => d.careMinutes)
      .map(d => {
        const cm = d.careMinutes;
        return [
          d.resident.id,
          d.resident.name,
          cm.totalMinutes,
          cm.rnMinutes,
          cm.targetMinutes,
          cm.targetRNMinutes,
          cm.compliant ? 'Yes' : 'No',
          cm.rnCompliant ? 'Yes' : 'No',
          cm.complianceGap,
          cm.rnComplianceGap
        ].join(',');
      });
    
    return [headers.join(','), ...rows].join('\n');
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleRecalculate = async () => {
    setLoading(true);
    try {
      // Simulate recalculation from roster
      await loadData();
      showToast('Care minutes recalculated successfully', 'success');
    } catch (error) {
      console.error('Error recalculating:', error);
      showToast('Error recalculating care minutes', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!summary || (summary.totalResidents === 0 && !loading)) {
    return (
      <div className="text-center p-8 text-gray-500">
        <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-lg mb-2 font-semibold text-gray-700">No care minutes data available</p>
        <p className="text-sm mb-6 text-gray-500">
          Care minutes data needs to be initialized. Click the button below to load data for all residents.
        </p>
        <div className="flex gap-4 justify-center">
          <button
            onClick={handleRefresh}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center gap-2 shadow-lg"
          >
            <RefreshCw className="w-4 h-4" />
            Initialize Care Minutes Data
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-4">
          This will load care minutes data from the last 14 days for all aged care residents.
        </p>
      </div>
    );
  }

  const complianceRate = summary.complianceRate || 0;
  const isCompliant = complianceRate >= 95;
  const avgMinutes = summary.averageMinutes || 0;
  const targetMinutes = 215;
  const avgRNMinutes = summary.averageRNMinutes || 0;
  const targetRNMinutes = 44;

  // Filter resident details
  const filteredDetails = residentDetails.filter(d => {
    const matchesSearch = !searchTerm || 
      d.resident.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.resident.room?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesWard = filterWard === 'all' || d.resident.ward === filterWard;
    return matchesSearch && matchesWard;
  });

  // Get unique wards
  const wards = [...new Set(residentDetails.map(d => d.resident.ward).filter(Boolean))];

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3 mb-2">
              <Clock className="w-8 h-8" />
              Care Minutes Compliance
            </h1>
            <p className="text-blue-100">Aged Care Act 2024 - Real-time compliance tracking</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all"
              title="Reload care minutes data from mock data"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh Data
            </button>
            <button
              onClick={handleRecalculate}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all"
            >
              <Activity className="w-4 h-4" />
              Recalculate
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Date Navigation */}
        <div className="flex items-center gap-4 bg-white bg-opacity-10 rounded-lg p-4">
          <button
            onClick={() => handleDateChange(-1)}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-4 py-2 bg-white bg-opacity-20 rounded-lg text-white font-semibold border-none focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
          />
          <button
            onClick={() => handleDateChange(1)}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
          >
            <ArrowRight className="w-5 h-5" />
          </button>
          <div className="flex-1"></div>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedView('today')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                selectedView === 'today' ? 'bg-white text-blue-600' : 'bg-white bg-opacity-20 text-white'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setSelectedView('week')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                selectedView === 'week' ? 'bg-white text-blue-600' : 'bg-white bg-opacity-20 text-white'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setSelectedView('month')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                selectedView === 'month' ? 'bg-white text-blue-600' : 'bg-white bg-opacity-20 text-white'
              }`}
            >
              Month
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-blue-300 transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-md">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <span className={`text-3xl font-bold ${isCompliant ? 'text-green-600' : 'text-red-600'}`}>
                {complianceRate.toFixed(1)}%
              </span>
              <div className="text-xs text-gray-500 mt-1">Compliance</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800 mb-2">Overall Compliance Rate</h3>
          <div className="flex items-center gap-2 text-sm">
            <div className={`flex-1 h-2 rounded-full ${isCompliant ? 'bg-green-100' : 'bg-red-100'}`}>
              <div
                className={`h-2 rounded-full ${isCompliant ? 'bg-green-600' : 'bg-red-600'}`}
                style={{ width: `${complianceRate}%` }}
              ></div>
            </div>
            <span className={`font-semibold ${isCompliant ? 'text-green-600' : 'text-red-600'}`}>
              {summary.fullyCompliantResidents}/{summary.totalResidents}
            </span>
          </div>
          <p className="text-xs text-gray-600 mt-2">
            Target: 95% | {isCompliant ? '✓ Compliant' : '⚠ At Risk'}
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-green-300 transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-md">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-800">{avgMinutes.toFixed(0)}</div>
              <div className="text-xs text-gray-500">min/day</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800 mb-2">Average Care Minutes</h3>
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 h-2 bg-gray-200 rounded-full">
              <div
                className={`h-2 rounded-full ${avgMinutes >= targetMinutes ? 'bg-green-600' : 'bg-red-600'}`}
                style={{ width: `${Math.min((avgMinutes / targetMinutes) * 100, 100)}%` }}
              ></div>
            </div>
            {avgMinutes >= targetMinutes ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-600" />
            )}
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Target: {targetMinutes} min</span>
            <span className={`font-semibold ${avgMinutes >= targetMinutes ? 'text-green-600' : 'text-red-600'}`}>
              {avgMinutes >= targetMinutes ? '+' : '-'}{Math.abs(avgMinutes - targetMinutes).toFixed(0)} min
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-purple-300 transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-md">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-800">{avgRNMinutes.toFixed(0)}</div>
              <div className="text-xs text-gray-500">RN min/day</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800 mb-2">Average RN Minutes</h3>
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 h-2 bg-gray-200 rounded-full">
              <div
                className={`h-2 rounded-full ${avgRNMinutes >= targetRNMinutes ? 'bg-green-600' : 'bg-red-600'}`}
                style={{ width: `${Math.min((avgRNMinutes / targetRNMinutes) * 100, 100)}%` }}
              ></div>
            </div>
            {avgRNMinutes >= targetRNMinutes ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-600" />
            )}
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Target: {targetRNMinutes} min</span>
            <span className={`font-semibold ${avgRNMinutes >= targetRNMinutes ? 'text-green-600' : 'text-red-600'}`}>
              {avgRNMinutes >= targetRNMinutes ? '+' : '-'}{Math.abs(avgRNMinutes - targetRNMinutes).toFixed(0)} min
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-orange-300 transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-md">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-red-600">{alerts.length}</div>
              <div className="text-xs text-gray-500">alerts</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800 mb-2">Compliance Alerts</h3>
          <div className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">High Priority</span>
              <span className="font-semibold text-red-600">
                {alerts.filter(a => a.severity === 'high').length}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Medium Priority</span>
              <span className="font-semibold text-yellow-600">
                {alerts.filter(a => a.severity === 'medium').length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search residents by name or room..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterWard}
            onChange={(e) => setFilterWard(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Wards</option>
            {wards.map(ward => (
              <option key={ward} value={ward}>{ward}</option>
            ))}
          </select>
          <button
            onClick={() => {
              setSearchTerm('');
              setFilterWard('all');
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Clear
          </button>
        </div>
      </div>

      {/* Resident-Level Details Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-600" />
              Resident-Level Compliance
            </h3>
            <div className="text-sm text-gray-600">
              {filteredDetails.length} residents
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 font-bold text-gray-700">Resident</th>
                <th className="text-left py-4 px-6 font-bold text-gray-700">Room</th>
                <th className="text-center py-4 px-6 font-bold text-gray-700">AN-ACC</th>
                <th className="text-right py-4 px-6 font-bold text-gray-700">Total Min</th>
                <th className="text-right py-4 px-6 font-bold text-gray-700">RN Min</th>
                <th className="text-right py-4 px-6 font-bold text-gray-700">Target</th>
                <th className="text-right py-4 px-6 font-bold text-gray-700">Gap</th>
                <th className="text-center py-4 px-6 font-bold text-gray-700">Status</th>
                <th className="text-center py-4 px-6 font-bold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredDetails.map((detail, index) => {
                const cm = detail.careMinutes;
                const resident = detail.resident;
                
                if (!cm) {
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-xl">
                            {resident.photo || '👤'}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-800">{resident.name}</div>
                            <div className="text-xs text-gray-500">{resident.careLevel || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-600">{resident.room || 'N/A'}</td>
                      <td colSpan="6" className="py-4 px-6 text-center text-gray-400 italic">
                        No care minutes data for this date
                      </td>
                      <td className="py-4 px-6 text-center">
                        <button
                          onClick={() => {
                            showToast('Manual entry feature coming soon', 'info');
                          }}
                          className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                        >
                          Add Data
                        </button>
                      </td>
                    </tr>
                  );
                }

                const isCompliant = cm.compliant && cm.rnCompliant;
                const gap = cm.complianceGap + cm.rnComplianceGap;

                return (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-xl">
                          {resident.photo || '👤'}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-800">{resident.name}</div>
                          <div className="text-xs text-gray-500">{resident.careLevel || 'N/A'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-600">{resident.room || 'N/A'}</td>
                    <td className="py-4 px-6 text-center">
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
                        Class {cm.anaccClass || 'N/A'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="font-semibold text-gray-800">{cm.totalMinutes}</div>
                      <div className="text-xs text-gray-500">
                        RN: {cm.rnMinutes} | EN: {cm.minutes?.en || 0} | PCA: {cm.minutes?.pca || 0}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className={`font-semibold ${cm.rnCompliant ? 'text-green-600' : 'text-red-600'}`}>
                        {cm.rnMinutes}
                      </div>
                      <div className="text-xs text-gray-500">Target: {cm.targetRNMinutes}</div>
                    </td>
                    <td className="py-4 px-6 text-right text-gray-600">
                      {cm.targetMinutes} min
                    </td>
                    <td className="py-4 px-6 text-right">
                      {gap > 0 ? (
                        <span className="text-red-600 font-semibold">-{gap} min</span>
                      ) : (
                        <span className="text-green-600 font-semibold">✓ Met</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-center">
                      {isCompliant ? (
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold flex items-center justify-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Compliant
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold flex items-center justify-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Non-Compliant
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setSelectedResident({ resident, careMinutes: cm })}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            showToast('Edit care minutes feature coming soon', 'info');
                          }}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-all"
                          title="Edit"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 shadow-lg border-2 border-red-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-red-800">Compliance Alerts</h3>
                <p className="text-sm text-red-600">Residents requiring immediate attention</p>
              </div>
            </div>
            <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full font-semibold">
              {alerts.length} Active
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {alerts.map((alert, index) => {
              const resident = residents.find(r => r.id === alert.residentId);
              return (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-2 ${
                    alert.severity === 'high'
                      ? 'bg-red-100 border-red-300'
                      : 'bg-yellow-100 border-yellow-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-bold text-gray-800">{resident?.name || `Resident ${alert.residentId}`}</h4>
                      <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs rounded-full font-semibold ${
                        alert.severity === 'high'
                          ? 'bg-red-200 text-red-900'
                          : 'bg-yellow-200 text-yellow-900'
                      }`}
                    >
                      {alert.severity.toUpperCase()}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                    <div>
                      <span className="text-gray-600">Total Gap:</span>
                      <span className="font-semibold text-red-600 ml-2">{alert.complianceGap} min</span>
                    </div>
                    <div>
                      <span className="text-gray-600">RN Gap:</span>
                      <span className="font-semibold text-red-600 ml-2">{alert.rnComplianceGap} min</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedResident({
                        resident: resident || { id: alert.residentId },
                        careMinutes: null
                      });
                    }}
                    className="mt-3 w-full px-3 py-2 bg-white hover:bg-gray-50 rounded-lg text-sm font-semibold text-gray-700 border border-gray-300 transition-all"
                  >
                    View Details & Take Action
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Trend Chart Section */}
      <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-blue-600" />
          Compliance Trends (Last 7 Days)
        </h3>
        <div className="h-64 flex items-end justify-between gap-2">
          {[1, 2, 3, 4, 5, 6, 7].map((day, index) => {
            const mockCompliance = 85 + Math.random() * 15; // 85-100%
            return (
              <div key={day} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg hover:from-blue-600 hover:to-blue-500 transition-all cursor-pointer"
                  style={{ height: `${mockCompliance}%` }}
                  title={`Day ${day}: ${mockCompliance.toFixed(1)}%`}
                ></div>
                <div className="text-xs text-gray-600 mt-2">Day {day}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <span>Average: {complianceRate.toFixed(1)}%</span>
          <span>Target: 95%</span>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Download className="w-6 h-6 text-blue-600" />
                Export Care Minutes Data
              </h2>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => handleExportData('csv')}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center justify-center gap-2 transition-all"
              >
                <FileText className="w-5 h-5" />
                Export as CSV
              </button>
              <button
                onClick={() => handleExportData('json')}
                className="w-full px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold flex items-center justify-center gap-2 transition-all"
              >
                <FileText className="w-5 h-5" />
                Export as JSON
              </button>
              <button
                onClick={() => handleExportData('pdf')}
                className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold flex items-center justify-center gap-2 transition-all"
              >
                <FileText className="w-5 h-5" />
                Export as PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resident Detail Modal */}
      {selectedResident && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {selectedResident.resident.name} - Care Minutes Details
              </h2>
              <button
                onClick={() => setSelectedResident(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            {selectedResident.careMinutes ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">Total Minutes</div>
                    <div className="text-2xl font-bold text-blue-600">
                      {selectedResident.careMinutes.totalMinutes}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Target: {selectedResident.careMinutes.targetMinutes}
                    </div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">RN Minutes</div>
                    <div className="text-2xl font-bold text-purple-600">
                      {selectedResident.careMinutes.rnMinutes}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Target: {selectedResident.careMinutes.targetRNMinutes}
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3">Breakdown by Role</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-gray-600">Registered Nurse (RN)</div>
                      <div className="text-lg font-semibold">{selectedResident.careMinutes.minutes?.rn || 0} min</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Enrolled Nurse (EN)</div>
                      <div className="text-lg font-semibold">{selectedResident.careMinutes.minutes?.en || 0} min</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Personal Care Assistant</div>
                      <div className="text-lg font-semibold">{selectedResident.careMinutes.minutes?.pca || 0} min</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Allied Health</div>
                      <div className="text-lg font-semibold">{selectedResident.careMinutes.minutes?.allied || 0} min</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No care minutes data for this resident</p>
                <button
                  onClick={() => {
                    showToast('Manual entry feature coming soon', 'info');
                  }}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                >
                  Add Care Minutes Data
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CareMinutesDashboard;
